import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ─────────────────────────────────────────────────────────────────────────────
// Engagement Notes — external touches (outreach, email, call, meeting, demo,
// LinkedIn, intro) captured by reps on an account, optionally scoped to a
// specific contact on that account.
//
// Engagement = every touch with a human outside our team.
// Activity = internal system events on the account row (see useAccountActivity).
// See docs/ACCOUNT_PAGE_GOLD_SHEET.md for the canonical routing table.
// ─────────────────────────────────────────────────────────────────────────────

export type EngagementType =
  | "outreach"
  | "email"
  | "call"
  | "meeting"
  | "demo"
  | "linkedin"
  | "intro"
  | "other";

export type EngagementOutcome =
  | "no_response"
  | "replied"
  | "interested"
  | "not_interested"
  | "booked"
  | "completed"
  | "other";

export interface EngagementNote {
  id: string;
  account_id: string;
  contact_id: string | null;
  proposed_contact_id: string | null;
  proposed_contact_confidence: number | null;
  engagement_type: EngagementType;
  engagement_date: string; // date (YYYY-MM-DD)
  attendees: string[] | null;
  outcome: EngagementOutcome | null;
  detail: string | null;
  source: "manual" | "migrated_from_notes";
  is_proposed: boolean;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  // Joined contact (hydrated below)
  contact?: { id: string; full_name: string | null; job_title: string | null } | null;
  proposed_contact?: { id: string; full_name: string | null; job_title: string | null } | null;
}

interface ContactMini {
  id: string;
  full_name: string | null;
  job_title: string | null;
}

export const ENGAGEMENT_TYPES: EngagementType[] = [
  "outreach",
  "email",
  "call",
  "meeting",
  "demo",
  "linkedin",
  "intro",
  "other",
];

export const ENGAGEMENT_OUTCOMES: EngagementOutcome[] = [
  "no_response",
  "replied",
  "interested",
  "not_interested",
  "booked",
  "completed",
  "other",
];

// ── List / read ────────────────────────────────────────────────────────────

export function useEngagementNotes(accountId: string | null) {
  return useQuery({
    queryKey: ["engagement-notes", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      if (!accountId) return [] as EngagementNote[];
      const { data, error } = await (supabase as any)
        .from("account_engagement_notes")
        .select("*")
        .eq("account_id", accountId)
        .order("engagement_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data || []) as EngagementNote[];

      // Hydrate contact names for both confirmed + proposed in one query.
      const contactIds = Array.from(
        new Set(
          rows.flatMap((r) =>
            [r.contact_id, r.proposed_contact_id].filter(Boolean) as string[]
          )
        )
      );
      if (!contactIds.length) return rows;

      const { data: contactsData, error: contactsErr } = await (supabase as any)
        .from("contacts")
        .select("id, full_name, job_title")
        .in("id", contactIds);
      if (contactsErr) throw contactsErr;
      const byId: Record<string, ContactMini> = {};
      (contactsData || []).forEach((c: ContactMini) => {
        byId[c.id] = c;
      });
      return rows.map((r) => ({
        ...r,
        contact: r.contact_id ? byId[r.contact_id] ?? null : null,
        proposed_contact: r.proposed_contact_id ? byId[r.proposed_contact_id] ?? null : null,
      }));
    },
  });
}

// ── Account contact list (for the combobox) ─────────────────────────────────

export function useAccountContacts(accountId: string | null) {
  return useQuery({
    queryKey: ["account-contacts-mini", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      if (!accountId) return [] as ContactMini[];
      const { data, error } = await (supabase as any)
        .from("contacts")
        .select("id, full_name, job_title")
        .eq("company_id", accountId)
        .order("full_name", { ascending: true });
      if (error) throw error;
      return (data || []) as ContactMini[];
    },
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export interface CreateEngagementNoteArgs {
  accountId: string;
  contactId?: string | null;
  engagementType: EngagementType;
  engagementDate: string;
  attendees?: string[] | null;
  outcome?: EngagementOutcome | null;
  detail?: string | null;
  performedBy?: { id: string | null; name: string | null };
}

export function useCreateEngagementNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateEngagementNoteArgs) => {
      const payload = {
        account_id: args.accountId,
        contact_id: args.contactId ?? null,
        engagement_type: args.engagementType,
        engagement_date: args.engagementDate,
        attendees: args.attendees ?? null,
        outcome: args.outcome ?? null,
        detail: args.detail ?? null,
        source: "manual",
        is_proposed: false,
        created_by: args.performedBy?.id ?? null,
        created_by_name: args.performedBy?.name ?? null,
      };
      const { data, error } = await (supabase as any)
        .from("account_engagement_notes")
        .insert(payload)
        .select("*")
        .single();
      if (error) throw error;
      return data as EngagementNote;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["engagement-notes", vars.accountId] });
      queryClient.invalidateQueries({ queryKey: ["account-campaign-timeline", vars.accountId] });
    },
  });
}

export interface UpdateEngagementNoteArgs {
  id: string;
  accountId: string;
  patch: Partial<
    Pick<
      EngagementNote,
      | "contact_id"
      | "engagement_type"
      | "engagement_date"
      | "attendees"
      | "outcome"
      | "detail"
      | "is_proposed"
    >
  >;
}

export function useUpdateEngagementNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateEngagementNoteArgs) => {
      const { error } = await (supabase as any)
        .from("account_engagement_notes")
        .update(patch)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["engagement-notes", vars.accountId] });
      queryClient.invalidateQueries({ queryKey: ["account-campaign-timeline", vars.accountId] });
    },
  });
}

export function useConfirmProposedEngagementNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      accountId,
      acceptProposedContact,
    }: {
      id: string;
      accountId: string;
      acceptProposedContact: boolean;
    }) => {
      // Promote proposed_contact_id to contact_id if the rep accepted it.
      let patch: Record<string, unknown> = { is_proposed: false };
      if (acceptProposedContact) {
        const { data: current, error: fetchErr } = await (supabase as any)
          .from("account_engagement_notes")
          .select("proposed_contact_id")
          .eq("id", id)
          .single();
        if (fetchErr) throw fetchErr;
        if (current?.proposed_contact_id) {
          patch = { ...patch, contact_id: current.proposed_contact_id };
        }
      }
      const { error } = await (supabase as any)
        .from("account_engagement_notes")
        .update(patch)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["engagement-notes", vars.accountId] });
      queryClient.invalidateQueries({ queryKey: ["account-campaign-timeline", vars.accountId] });
    },
  });
}

export function useDeleteEngagementNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; accountId: string }) => {
      const { error } = await (supabase as any)
        .from("account_engagement_notes")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["engagement-notes", vars.accountId] });
      queryClient.invalidateQueries({ queryKey: ["account-campaign-timeline", vars.accountId] });
    },
  });
}

// ── Minimal contact creation (inline "Add new contact") ─────────────────────

export interface CreateContactMiniArgs {
  accountId: string;
  firstName: string;
  lastName: string;
  jobTitle?: string | null;
  email?: string | null;
  linkedinUrl?: string | null;
  accountName?: string | null;
}

export function useCreateContactForAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateContactMiniArgs) => {
      const fullName = [args.firstName, args.lastName].filter(Boolean).join(" ").trim();
      const payload = {
        company_id: args.accountId,
        company_name: args.accountName ?? null,
        first_name: args.firstName || null,
        last_name: args.lastName || null,
        full_name: fullName || null,
        job_title: args.jobTitle || null,
        email: args.email || null,
        linkedin_url: args.linkedinUrl || null,
        source: "manual_engagement",
      };
      const { data, error } = await (supabase as any)
        .from("contacts")
        .insert(payload)
        .select("id, full_name, job_title")
        .single();
      if (error) throw error;
      return data as ContactMini;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["account-contacts-mini", vars.accountId] });
    },
  });
}
